/**
 * 认证和会话管理工具
 * 基于Auth1页面的登录授权机制
 */

import Cookies from 'js-cookie';
import { showToast } from '@/utils/toast';
import router from '@/router';

/**
 * 检查用户是否已登录
 * 检查多个存储位置以确保准确性
 */
export function isUserLoggedIn(): boolean {
  // 检查 Cookie（主要方式）
  const userIdFromCookie = Cookies.get('DedeUserID');
  const userNameFromCookie = Cookies.get('UserName');
  
  // 检查 localStorage
  const hasRegistered = localStorage.getItem('hasRegistered');
  const token = localStorage.getItem('token');
  const userInfo = localStorage.getItem('userInfo');
  
  // 检查 sessionStorage
  const sessionToken = sessionStorage.getItem('token');
  
  // 如果任意一个存在，则认为已登录
  return !!(
    userIdFromCookie || 
    userNameFromCookie || 
    hasRegistered === 'true' || 
    token || 
    sessionToken || 
    userInfo
  );
}

/**
 * 获取当前登录用户的信息
 */
export function getCurrentUser() {
  return {
    userId: Cookies.get('DedeUserID'),
    userName: Cookies.get('UserName'),
    hasRegistered: localStorage.getItem('hasRegistered') === 'true'
  };
}

/**
 * 要求用户登录
 * 如果用户未登录，保存当前路径并跳转到登录页
 * 如果用户已登录且提供了redirectPath，则直接跳转到该页面
 * @param redirectPath 登录成功后要跳转的路径（可选，默认为当前路径）
 * @param showMessage 是否显示提示消息（默认为true）
 * @returns 如果用户已登录返回true，否则返回false并跳转登录页
 */
export function requireLogin(redirectPath?: string, showMessage: boolean = true): boolean {
  if (isUserLoggedIn()) {
    // 已登录，如果指定了跳转路径则直接跳转
    if (redirectPath) {
      router.push(redirectPath);
    }
    return true;
  }
  
  // 显示提示消息
  if (showMessage) {
    showToast('请先登录');
  }
  
  // 保存目标路径到 sessionStorage
  const targetPath = redirectPath || window.location.pathname;
  sessionStorage.setItem('redirectAfterLogin', targetPath);
  
  // 延迟跳转，让用户看到提示
  setTimeout(() => {
    router.push({ path: '/Auth1', query: { type: 'login' } });
  }, 1000);
  
  return false;
}

/**
 * 登录成功后的处理
 * 保存用户信息到Cookie和localStorage
 * @param userData 用户数据（包含DedeUserID和UserName，以及可选的token和user）
 * @param expiresInDays Cookie过期天数（默认30天）
 */
export function handleLoginSuccess(userData: { 
  DedeUserID?: string; 
  UserName?: string;
  token?: string;
  user?: any;
}, expiresInDays: number = 30) {
  // 【方案A统一】优先保存新机制的 JWT Token
  if (userData.token) {
    localStorage.setItem('token', userData.token);
  }
  if (userData.user) {
    localStorage.setItem('userInfo', JSON.stringify(userData.user));
  }

  // 兼容旧机制：保存到 Cookie
  if (userData.DedeUserID) {
    Cookies.set('DedeUserID', userData.DedeUserID, { expires: expiresInDays });
  }
  if (userData.UserName) {
    Cookies.set('UserName', userData.UserName, { expires: expiresInDays });
  }
  
  // 保存到 localStorage
  localStorage.setItem('hasRegistered', 'true');
  
  showToast('登录成功');
  
  // 检查是否有保存的跳转路径
  const redirectPath = sessionStorage.getItem('redirectAfterLogin');
  if (redirectPath) {
    // 清除保存的路径
    sessionStorage.removeItem('redirectAfterLogin');
    // 跳转到保存的路径
    router.push(redirectPath);
  } else {
    // 【方案A统一】默认跳转到工作台
    router.push('/home');
  }
}

/**
 * 登出
 * 清除所有用户会话信息，并调用后端退出接口
 * @param redirectToHome 是否跳转到首页（默认为true）
 */
export async function logout(redirectToHome: boolean = true) {
  const API_BASE_URL = import.meta.env.VITE_API_URL || '';
  
  try {
    // 【方案A统一】调用后端退出接口，与旧机制保持一致
    await fetch(`${API_BASE_URL}member/index_do.php?fmdo=login&dopost=exit`, {
      method: 'GET',
      credentials: 'include', // 确保携带 Cookie
    });
  } catch (error) {
    console.error('调用退出接口失败:', error);
    // 即使接口调用失败，也继续清理本地状态
  }
  
  // 清除 Cookie（兼容旧机制）
  Cookies.remove('DedeUserID');
  Cookies.remove('UserName');
  
  // 清除 localStorage（新机制 JWT）
  localStorage.removeItem('hasRegistered');
  localStorage.removeItem('token');
  localStorage.removeItem('userInfo');
  
  // 清除 sessionStorage
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('redirectAfterLogin');
  
  showToast('已退出登录');
  
  if (redirectToHome) {
    router.push('/');
  }
}

/**
 * 验证码登录
 * @param phoneNumber 手机号
 * @param verificationCode 验证码
 */
export async function loginWithCode(phoneNumber: string, verificationCode: string) {
  const API_BASE_URL = import.meta.env.VITE_API_URL || '';
  
  try {
    // 1. 先验证验证码
    const verifyResponse = await fetch(`${API_BASE_URL}api/sms/verifyCode`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        phone: phoneNumber,
        type: 'login',
        code: verificationCode,
      }).toString(),
    });

    const verifyData = await verifyResponse.json();

    if (!verifyData.success) {
      showToast(verifyData.message || '验证码错误或已失效');
      return false;
    }
    
    return true;
  } catch (error) {
    showToast('验证码校验请求失败');
    console.error('验证码校验错误:', error);
    return false;
  }
}

/**
 * 发送验证码
 * @param phoneNumber 手机号
 * @param type 验证码类型（login, register, reset）
 */
export async function sendVerificationCode(phoneNumber: string, type: 'login' | 'register' | 'reset' = 'login') {
  const API_BASE_URL = import.meta.env.VITE_API_URL || '';
  
  if (!/^1[3-9]\d{9}$/.test(phoneNumber)) {
    showToast('请输入正确的手机号');
    return false;
  }
  
  const urlMap = {
    login: `${API_BASE_URL}api/sms/sendLoginCode`,
    register: `${API_BASE_URL}api/sms/sendRegisterCode`,
    reset: `${API_BASE_URL}api/sms/sendResetCode`
  };
  
  try {
    const response = await fetch(urlMap[type], {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: `phone=${encodeURIComponent(phoneNumber)}`,
    });

    const data = await response.json();

    if (data.success) {
      showToast(data.message || '验证码已发送');
      return true;
    } else {
      showToast(data.message || '发送验证码失败');
      return false;
    }
  } catch (error) {
    showToast('发送验证码请求失败');
    console.error('发送验证码错误:', error);
    return false;
  }
}

/**
 * 【方案A统一】应用状态类型
 * 用于控制用户在不同阶段可访问的功能
 */
export type ApplicationStatus = 
  | 'none'           // 未报名
  | 'pending'        // 已报名待审核
  | 'reviewing'      // 审核中（初审/再核）
  | 'interview'      // 面谈阶段
  | 'training'       // 培训阶段
  | 'examination'    // 考核阶段
  | 'lottery'        // 抽签阶段
  | 'discussion'     // 座谈阶段
  | 'confirmed'      // 已确名
  | 'signing'        // 签约阶段
  | 'building'       // 建设阶段
  | 'operating';     // 开业运营

/**
 * 【方案A统一】获取当前用户的申请状态
 * 从localStorage中读取缓存的状态
 */
export function getApplicationStatus(): ApplicationStatus {
  const status = localStorage.getItem('applicationStatus');
  return (status as ApplicationStatus) || 'none';
}

/**
 * 【方案A统一】保存用户的申请状态
 */
export function setApplicationStatus(status: ApplicationStatus) {
  localStorage.setItem('applicationStatus', status);
}

/**
 * 【方案A统一】检查用户是否可以访问指定功能
 * @param feature 功能名称
 * @returns 是否有权限访问
 */
export function canAccessFeature(feature: 'registration' | 'training' | 'signing' | 'personal'): boolean {
  const status = getApplicationStatus();
  
  const accessMap: Record<ApplicationStatus, string[]> = {
    'none': ['registration'],  // 未报名只能报名
    'pending': ['registration', 'personal'],  // 已报名可查看进度
    'reviewing': ['personal'],  // 审核中
    'interview': ['personal'],  // 面谈
    'training': ['training', 'personal'],  // 培训阶段可访问培训
    'examination': ['training', 'personal'],  // 考核阶段
    'lottery': ['personal'],  // 抽签
    'discussion': ['personal'],  // 座谈
    'confirmed': ['signing', 'personal'],  // 确名后可签约
    'signing': ['signing', 'personal'],  // 签约阶段
    'building': ['personal'],  // 建设阶段
    'operating': ['training', 'signing', 'personal'],  // 完全权限
  };
  
  const allowedFeatures = accessMap[status] || [];
  return allowedFeatures.includes(feature);
}

/**
 * 【方案A统一】检查并引导用户到正确的功能页面
 * @param feature 目标功能
 * @param showTip 是否显示提示
 * @returns 是否允许访问
 */
export function checkAndRedirect(feature: 'registration' | 'training' | 'signing' | 'personal', showTip: boolean = true): boolean {
  // 首先检查登录状态
  if (!isUserLoggedIn()) {
    requireLogin();
    return false;
  }
  
  const status = getApplicationStatus();
  const canAccess = canAccessFeature(feature);
  
  if (!canAccess && showTip) {
    const tipMessages: Record<ApplicationStatus, Record<string, string>> = {
      'none': {
        'training': '请先完成报名',
        'signing': '请先完成报名',
      },
      'pending': {
        'training': '请等待审核通过后参加培训',
        'signing': '请完成培训后再进行签约',
      },
      'reviewing': {
        'training': '审核中，请耐心等待',
        'signing': '请完成培训后再进行签约',
        'registration': '您已报名，正在审核中',
      },
      'interview': {
        'training': '请完成面谈后参加培训',
        'signing': '请完成培训后再进行签约',
        'registration': '您已报名，请参加面谈',
      },
      'training': {
        'signing': '请完成培训考核后再签约',
        'registration': '您已报名，请参加培训',
      },
      'examination': {
        'signing': '请完成考核后再签约',
        'registration': '您已报名，正在考核中',
      },
      'lottery': {
        'training': '培训已完成',
        'signing': '请等待抽签结果',
        'registration': '您已报名',
      },
      'discussion': {
        'training': '培训已完成',
        'signing': '请完成座谈后再签约',
        'registration': '您已报名',
      },
      'confirmed': {
        'training': '培训已完成',
        'registration': '您已确名，请进行签约',
      },
      'signing': {
        'training': '培训已完成',
        'registration': '您已在签约阶段',
      },
      'building': {
        'registration': '您的项目正在建设中',
      },
      'operating': {},
    };
    
    const message = tipMessages[status]?.[feature] || '当前阶段无法访问该功能';
    showToast(message);
  }
  
  return canAccess;
}

